import React from "react";
import MaterialModal from "../../../app/material-modal";
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { useQueryClient } from "@tanstack/react-query";
import { inputCommonStyle } from "../../../../util/admin/cssCommonStyle";
import { INVOICE_STATUS, INVOICE_TYPE } from "../../../../constants";
import { useParams } from "react-router-dom";
import { useKeycloak } from "@react-keycloak/web";
import { useCreateInvoice } from "../../../../tanstackQuery/queries/superAdmin/invoice";
import { toast } from "sonner";

const CreateInvoiceModal = ({ open, handleClose }) => {
  const client = useQueryClient();
  const products = client.getQueryData(["paywallProducts"]);
  const { orgID } = useParams();
  const { keycloak } = useKeycloak();

   const {
      mutateAsync: createInvoice,
      isPending,
      isSuccess,
    } = useCreateInvoice(
      { accessToken: keycloak?.token },
      {
        onSuccess: () => {
          client.invalidateQueries({ queryKey: ["get-invoices"] });
          handleClose()
        },
      }
    );


  const formik = useFormik({
    initialValues: {
      name: "",
      quantity: "",
      productID: "",
      type: "",
      status: "",
      project : "None Given"
    },
    onSubmit: (values) => {
      handleCreateInvoice(values);
    },
  });

  const handleCreateInvoice =async (values) =>{
    const { quantity, productID, ...rest } = values;

    const payload = {
      ...rest,
      orgID,
      products: [
        {
          productID,
          quantity,
        },
      ],
    };
    
    try {
      await createInvoice(payload);
      if (isSuccess) {
        formik.resetForm()
        toast.success("New Invoice successfully added!");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to add new invoice.");
    }
  }

  return (
    <MaterialModal
      open={open}
      handleDialogClose={handleClose}
      title="Create Invoice"
      styleOverrides={{
        container: {
          "& .MuiDialog-paper": {
            minHeight: "200px",
            minWidth: "500px",
            ".MuiDialogContent-root": {
              overflow: "auto",
            },
          },
        },
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Stack direction="column" justifyContent="space-around" gap={2}>
          {/* Invoice Name */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Invoice Name</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <TextField
                name="name"
                required
                variant="outlined"
                size="small"
                fullWidth
                sx={inputCommonStyle}
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && !formik.values.name}
                helperText={
                  formik.touched.name && !formik.values.name ? "Required" : ""
                }
              />
            </Box>
          </Stack>

          {/* Product Amount */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Product Amount</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <TextField
                name="quantity"
                required
                type="number"
                variant="outlined"
                size="small"
                fullWidth
                sx={inputCommonStyle}
                value={formik.values.quantity}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantity && !formik.values.quantity}
                helperText={
                  formik.touched.quantity && !formik.values.quantity
                    ? "Required"
                    : ""
                }
              />
            </Box>
          </Stack>

          {/* Product Type */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Product Type</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <FormControl
                size="small"
                required
                fullWidth
                sx={inputCommonStyle}
                error={formik.touched.productID && !formik.values.productID}
              >
                <InputLabel id="productID-label">Select Product</InputLabel>
                <Select
                  labelId="productID-label"
                  name="productID"
                  label="Select Product"
                  value={formik.values.productID}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    <em>Select Product</em>
                  </MenuItem>
                  {products?.map((product) => (
                    <MenuItem
                      key={product?.productID}
                      value={product?.productID}
                    >
                      {product?.name}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.productID && !formik.values.productID && (
                  <Typography variant="caption" color="error">
                    Required
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Stack>

          {/* Invoice Type */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Invoice Type</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <FormControl
                size="small"
                required
                fullWidth
                sx={inputCommonStyle}
                error={formik.touched.type && !formik.values.type}
              >
                <InputLabel id="type-label">Select Invoice Type</InputLabel>
                <Select
                  labelId="type-label"
                  name="type"
                  label="Select Invoice Type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    <em>Select Invoice Type</em>
                  </MenuItem>
                  {INVOICE_TYPE?.map((type, index) => (
                    <MenuItem key={`type-${index}`} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.type && !formik.values.type && (
                  <Typography variant="caption" color="error">
                    Required
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Stack>

          {/* Invoice Status */}
          <Stack direction="row" alignItems="center">
            <Box sx={{ flexBasis: "40%" }}>
              <Typography sx={{ fontWeight: "bold" }}>Invoice Status</Typography>
            </Box>
            <Box sx={{ flexBasis: "60%" }}>
              <FormControl
                size="small"
                required
                fullWidth
                sx={inputCommonStyle}
                error={formik.touched.status && !formik.values.status}
              >
                <InputLabel id="status-label">Select Invoice Status</InputLabel>
                <Select
                  labelId="status-label"
                  name="status"
                  label="Select Invoice Status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                >
                  <MenuItem value="" disabled>
                    <em>Select Invoice Status</em>
                  </MenuItem>
                  {INVOICE_STATUS?.map((status, index) => (
                    <MenuItem key={`status-${index}`} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
                {formik.touched.status && !formik.values.status && (
                  <Typography variant="caption" color="error">
                    Required
                  </Typography>
                )}
              </FormControl>
            </Box>
          </Stack>

          {/* Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 3 }}>
            <Button variant="outlined" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isPending}
              endIcon={isPending ? <CircularProgress size={16} /> : null}
            >
              Create Invoice
            </Button>
          </Box>
        </Stack>
      </form>
    </MaterialModal>
  );
};

export default CreateInvoiceModal;
